import React, { useState, useEffect, useContext } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  Loader,
  Film,
  Gamepad2,
  PartyPopper,
  Coffee,
  Dumbbell,
  GraduationCap,
  MoreHorizontal,
  Eye,
  UserCheck,
  UserX,
  HelpCircle
} from 'lucide-react';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { useSocket } from '../../context/SocketContext';
import * as eventService from '../../services/eventService';
import * as friendsService from '../../services/friendsService';
import Modal from '../../components/ui/Modal';

const EventManagement = () => {
  const { admin } = useContext(AdminAuthContext);
  const socket = useSocket();
  const { on } = socket;

  // Stati
  const [events, setEvents] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedFriends, setSelectedFriends] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    eventType: 'altro',
    color: '#3B82F6',
    maxParticipants: '',
    isPublic: true,
    notifyBefore: 60,
    invitedUserIds: []
  });

  // Tipi di evento con icone e colori
  const eventTypes = [
    { id: 'anime', label: 'Anime', icon: Film, color: '#EC4899', bgClass: 'bg-pink-500/20', textClass: 'text-pink-600 dark:text-pink-400' },
    { id: 'gaming', label: 'Gaming', icon: Gamepad2, color: '#8B5CF6', bgClass: 'bg-purple-500/20', textClass: 'text-purple-600 dark:text-purple-400' },
    { id: 'uscita', label: 'Uscita', icon: Users, color: '#10B981', bgClass: 'bg-green-500/20', textClass: 'text-green-600 dark:text-green-400' },
    { id: 'cinema', label: 'Cinema', icon: Film, color: '#F59E0B', bgClass: 'bg-amber-500/20', textClass: 'text-amber-600 dark:text-amber-400' },
    { id: 'cena', label: 'Cena', icon: Coffee, color: '#EF4444', bgClass: 'bg-red-500/20', textClass: 'text-red-600 dark:text-red-400' },
    { id: 'festa', label: 'Festa', icon: PartyPopper, color: '#F97316', bgClass: 'bg-orange-500/20', textClass: 'text-orange-600 dark:text-orange-400' },
    { id: 'sport', label: 'Sport', icon: Dumbbell, color: '#06B6D4', bgClass: 'bg-cyan-500/20', textClass: 'text-cyan-600 dark:text-cyan-400' },
    { id: 'studio', label: 'Studio', icon: GraduationCap, color: '#6366F1', bgClass: 'bg-indigo-500/20', textClass: 'text-indigo-600 dark:text-indigo-400' },
    { id: 'altro', label: 'Altro', icon: MoreHorizontal, color: '#6B7280', bgClass: 'bg-gray-500/20', textClass: 'text-gray-600 dark:text-gray-400' }
  ];

  useEffect(() => {
    if (admin?.id) {
      loadEvents();
      loadFriends();
    }
  }, [admin, filterStatus]);

  // Listener WebSocket per aggiornamenti real-time eventi
  useEffect(() => {
    if (!on || !admin?.id) return;

    const handleEventChange = (data) => {
      console.log('🔔 ADMIN - Event changed ricevuto:', data);
      console.log('🔔 ADMIN - Action:', data.action);
      console.log('🔔 ADMIN - Event data:', data.event);
      
      // Ricarica eventi quando c'è un cambiamento
      loadEvents();
      
      // Se la modale di dettaglio è aperta e l'evento aggiornato è quello visualizzato, aggiornalo
      if (showDetailModal && selectedEvent && data.event?.id === selectedEvent.id) {
        console.log('🔔 ADMIN - Aggiorno evento nella modale dettaglio');
        setSelectedEvent(data.event);
      }
    };

    console.log('🔔 ADMIN - Sottoscrivo a event-changed per admin', admin.id);
    
    // DEBUG: Ascolta TUTTI gli eventi per vedere cosa arriva
    const socketInstance = socket.getSocket?.();
    if (socketInstance) {
      socketInstance.onAny((eventName, ...args) => {
        console.log('🔔 DEBUG - Evento WebSocket ricevuto:', eventName, args);
      });
    }
    
    const cleanup = on('event-changed', handleEventChange);
    return cleanup;
  }, [on, admin, socket, showDetailModal, selectedEvent]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterStatus === 'upcoming') filters.upcoming = true;
      if (filterStatus === 'past') filters.past = true;
      if (filterStatus !== 'all' && filterStatus !== 'upcoming' && filterStatus !== 'past') {
        filters.status = filterStatus;
      }

      const data = await eventService.getEvents(admin.id, filters);
      console.log('📋 DEBUG - Risposta completa:', data);
      console.log('📋 DEBUG - Eventi caricati:', data.data);
      console.log('📋 DEBUG - Primo evento (se esiste):', data.data?.[0]);
      console.log('📋 DEBUG - Partecipanti primo evento:', data.data?.[0]?.participants);
      setEvents(data.data || []);
    } catch (error) {
      console.error('Errore caricamento eventi:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFriends = async () => {
    try {
      const data = await friendsService.getFriends(admin.id);
      setFriends(data.data || []);
    } catch (error) {
      console.error('Errore caricamento amici:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      eventDate: '',
      startTime: '',
      endTime: '',
      eventType: 'altro',
      color: '#3B82F6',
      maxParticipants: '',
      isPublic: true,
      notifyBefore: 60,
      invitedUserIds: []
    });
    setSelectedFriends([]);
  };

  const handleCreateEvent = async () => {
    if (!formData.title.trim() || !formData.eventDate) {
      alert('Titolo e data sono obbligatori');
      return;
    }

    try {
      const invitedUserIds = Array.from(selectedFriends);
      console.log('🔍 DEBUG - Amici selezionati:', invitedUserIds);
      console.log('🔍 DEBUG - selectedFriends Set:', selectedFriends);
      
      const eventData = {
        adminId: admin.id,
        ...formData,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
        invitedUserIds
      };
      
      console.log('🔍 DEBUG - Payload inviato al server:', eventData);
      
      const result = await eventService.createEvent(eventData);
      console.log('✅ DEBUG - Risposta dal server:', result);
      console.log('✅ DEBUG - Evento creato:', result.event);
      console.log('✅ DEBUG - Partecipanti nell\'evento:', result.event?.participants);

      await loadEvents();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('❌ Errore creazione evento:', error);
      console.error('❌ Dettagli errore:', error.response?.data);
      alert('Errore nella creazione dell\'evento');
    }
  };

  const handleEditEvent = async () => {
    if (!selectedEvent) return;

    try {
      // Aggiorna l'evento
      await eventService.updateEvent(selectedEvent.id, {
        adminId: admin.id,
        ...formData,
        maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null
      });

      // Gestisci inviti: trova partecipanti già esistenti
      const existingParticipantIds = selectedEvent.participants?.map(p => p.userId) || [];
      const newInvites = Array.from(selectedFriends).filter(id => !existingParticipantIds.includes(id)); // Converti Set in Array

      // Se ci sono nuovi inviti, invitali
      if (newInvites.length > 0) {
        await eventService.inviteParticipants(selectedEvent.id, admin.id, newInvites);
      }

      await loadEvents();
      setShowEditModal(false);
      setSelectedEvent(null);
      resetForm();
    } catch (error) {
      console.error('Errore modifica evento:', error);
      alert('Errore nella modifica dell\'evento');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo evento?')) return;

    try {
      await eventService.deleteEvent(eventId, admin.id);
      await loadEvents();
    } catch (error) {
      console.error('Errore eliminazione evento:', error);
      alert('Errore nell\'eliminazione dell\'evento');
    }
  };

  const openDetailModal = (event) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
  };

  const toggleFriendSelection = (friendId) => {
    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      }
      return [...prev, friendId];
    });
  };

  const selectAllFriends = () => {
    setSelectedFriends(friends.map(f => f.id));
  };

  const deselectAllFriends = () => {
    setSelectedFriends([]);
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      location: event.location || '',
      eventDate: event.eventDate?.split('T')[0] || '',
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      eventType: event.eventType,
      color: event.color || '#3B82F6',
      maxParticipants: event.maxParticipants || '',
      isPublic: event.isPublic,
      notifyBefore: event.notifyBefore || 60,
      invitedUserIds: []
    });
    // Imposta amici già invitati come selezionati
    const invitedIds = event.participants?.map(p => p.userId) || [];
    setSelectedFriends(invitedIds);
    setShowEditModal(true);
  };

  const getEventTypeInfo = (typeId) => {
    return eventTypes.find(t => t.id === typeId) || eventTypes[eventTypes.length - 1];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isUpcoming = (event) => {
    return new Date(event.eventDate) > new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-light-primary dark:text-dark-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6 animate-fade-in opacity-0"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-light-primary/10 dark:bg-dark-primary/10">
              <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-light-primary dark:text-dark-primary" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-light-on-surface dark:text-dark-on-surface">
                Gestione Eventi
              </h2>
              <p className="mt-0.5 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Organizza e condividi eventi
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-all shadow-md hover:shadow-lg"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">Nuovo Evento</span>
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg p-3 sm:p-4 animate-fade-in opacity-0"
        style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'all', label: 'Tutti' },
            { id: 'upcoming', label: 'Prossimi' },
            { id: 'scheduled', label: 'Programmati' },
            { id: 'completed', label: 'Completati' },
            { id: 'cancelled', label: 'Annullati' }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setFilterStatus(filter.id)}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterStatus === filter.id
                  ? 'bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary shadow-md'
                  : 'bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista Eventi */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {events.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-light-surface-container dark:bg-dark-surface-container rounded-lg">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-50" />
            <p className="text-light-on-surface-variant dark:text-dark-on-surface-variant">
              Nessun evento trovato
            </p>
          </div>
        ) : (
          events.map((event, index) => {
            const typeInfo = getEventTypeInfo(event.eventType);
            const TypeIcon = typeInfo.icon;
            const upcoming = isUpcoming(event);

            return (
              <div
                key={event.id}
                className={`
                  ${typeInfo.bgClass}
                  rounded-lg shadow-surface-container p-3 sm:p-4 border-2 border-light-outline dark:border-dark-outline
                  animate-fade-in opacity-0 hover:shadow-lg transition-all
                `}
                style={{
                  animationDelay: `${300 + index * 50}ms`,
                  animationFillMode: 'forwards'
                }}
              >
                {/* Header Card */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <TypeIcon className={`w-5 h-5 flex-shrink-0 ${typeInfo.textClass}`} />
                    <span className={`text-xs font-medium truncate ${typeInfo.textClass}`}>
                      {typeInfo.label}
                    </span>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    <button
                      onClick={() => openDetailModal(event)}
                      className="p-2 rounded-lg hover:bg-light-primary/20 dark:hover:bg-dark-primary/20 transition-colors"
                      title="Vedi partecipanti"
                    >
                      <Eye className="w-4 h-4 text-light-primary dark:text-dark-primary" />
                    </button>
                    <button
                      onClick={() => openEditModal(event)}
                      className="p-2 rounded-lg hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant transition-colors"
                      title="Modifica"
                    >
                      <Edit2 className="w-4 h-4 text-light-on-surface dark:text-dark-on-surface" />
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="p-2 rounded-lg hover:bg-red-500/20 transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Titolo */}
                <h3 className="text-lg font-bold text-light-on-surface dark:text-dark-on-surface mb-2 line-clamp-2">
                  {event.title}
                </h3>

                {/* Descrizione */}
                {event.description && (
                  <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mb-3 line-clamp-2">
                    {event.description}
                  </p>
                )}

                {/* Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-light-on-surface-variant dark:text-dark-on-surface-variant">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(event.eventDate)}</span>
                    {upcoming && (
                      <span className="ml-auto text-xs px-2 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full">
                        Prossimo
                      </span>
                    )}
                  </div>

                  {(event.startTime || event.endTime) && (
                    <div className="flex items-center gap-2 text-light-on-surface-variant dark:text-dark-on-surface-variant">
                      <Clock className="w-4 h-4" />
                      <span>
                        {event.startTime && event.startTime.substring(0, 5)}
                        {event.startTime && event.endTime && ' - '}
                        {event.endTime && event.endTime.substring(0, 5)}
                      </span>
                    </div>
                  )}

                  {event.location && (
                    <div className="flex items-center gap-2 text-light-on-surface-variant dark:text-dark-on-surface-variant">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{event.location}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-light-on-surface-variant dark:text-dark-on-surface-variant">
                    <Users className="w-4 h-4" />
                    <span>
                      {event.participantCount || 0} invitati ({event.acceptedCount || 0} accettati)
                      {event.maxParticipants && ` / max ${event.maxParticipants}`}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal Creazione */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); resetForm(); }}
        title="Crea Nuovo Evento"
        icon={Calendar}
        iconColor="text-light-primary dark:text-dark-primary"
        size="lg"
      >
        <div className="space-y-4">
                {/* Titolo */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Titolo *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    placeholder="Es. Serata Anime"
                  />
                </div>

                {/* Tipo Evento */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Tipo Evento *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {eventTypes.map(type => {
                      const TypeIcon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => handleFormChange('eventType', type.id)}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all
                            ${formData.eventType === type.id
                              ? `${type.bgClass} border-current ${type.textClass}`
                              : 'border-light-outline dark:border-dark-outline text-light-on-surface-variant dark:text-dark-on-surface-variant hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant'
                            }
                          `}
                        >
                          <TypeIcon className="w-4 h-4" />
                          <span className="text-sm">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Data e Orari */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Data *
                    </label>
                    <input
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => handleFormChange('eventDate', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Ora Inizio
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleFormChange('startTime', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Ora Fine (opzionale)
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleFormChange('endTime', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                      placeholder="Lascia vuoto se non rilevante"
                    />
                  </div>
                </div>

                {/* Descrizione */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Descrizione
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none resize-none"
                    placeholder="Dettagli sull'evento..."
                  />
                </div>

                {/* Luogo */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Luogo
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleFormChange('location', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    placeholder="Es. Casa mia, Discord, Cinema"
                  />
                </div>

                {/* Max Partecipanti */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Max Partecipanti (opzionale)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxParticipants}
                    onChange={(e) => handleFormChange('maxParticipants', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    placeholder="Lascia vuoto per illimitato"
                  />
                </div>

                {/* Pubblico/Privato */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={formData.isPublic}
                    onChange={(e) => handleFormChange('isPublic', e.target.checked)}
                    className="w-4 h-4 text-light-primary dark:text-dark-primary rounded"
                  />
                  <label htmlFor="isPublic" className="text-sm text-light-on-surface dark:text-dark-on-surface">
                    Visibile a tutti gli amici
                  </label>
                </div>

                {/* Invita Amici */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface">
                      Invita Amici ({selectedFriends.length} selezionati)
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllFriends}
                        className="text-xs text-light-primary dark:text-dark-primary hover:underline"
                      >
                        Tutti
                      </button>
                      <button
                        type="button"
                        onClick={deselectAllFriends}
                        className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant hover:underline"
                      >
                        Nessuno
                      </button>
                    </div>
                  </div>
                  {friends.length === 0 ? (
                    <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant italic">
                      Nessun amico da invitare. Aggiungi amici dal pannello gestione amici.
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-light-outline dark:border-dark-outline rounded-lg p-2 space-y-1">
                      {friends.map(friend => (
                        <label
                          key={friend.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFriends.includes(friend.id)}
                            onChange={() => toggleFriendSelection(friend.id)}
                            className="w-4 h-4 text-light-primary dark:text-dark-primary rounded"
                          />
                          <span className="text-sm text-light-on-surface dark:text-dark-on-surface">
                            {friend.firstName} {friend.lastName} ({friend.username})
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Azioni Modal */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 transition-opacity"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCreateEvent}
                  className="flex-1 px-4 py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Crea Evento
                </button>
              </div>
        </Modal>

      {/* Modal Modifica (stesso layout del modal creazione) */}
      <Modal
        isOpen={showEditModal && selectedEvent}
        onClose={() => { setShowEditModal(false); setSelectedEvent(null); resetForm(); }}
        title="Modifica Evento"
        icon={Edit2}
        iconColor="text-light-primary dark:text-dark-primary"
        size="lg"
      >
        <div className="space-y-4">
                {/* Stesso form del modal creazione */}
                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Titolo *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-2">
                    Tipo Evento *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {eventTypes.map(type => {
                      const TypeIcon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => handleFormChange('eventType', type.id)}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all
                            ${formData.eventType === type.id
                              ? `${type.bgClass} border-current ${type.textClass}`
                              : 'border-light-outline dark:border-dark-outline text-light-on-surface-variant dark:text-dark-on-surface-variant hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant'
                            }
                          `}
                        >
                          <TypeIcon className="w-4 h-4" />
                          <span className="text-sm">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Data *
                    </label>
                    <input
                      type="date"
                      value={formData.eventDate}
                      onChange={(e) => handleFormChange('eventDate', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Ora Inizio
                    </label>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => handleFormChange('startTime', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                      Ora Fine (opzionale)
                    </label>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => handleFormChange('endTime', e.target.value)}
                      className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                      placeholder="Lascia vuoto se non rilevante"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Descrizione
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Luogo
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleFormChange('location', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface mb-1">
                    Max Partecipanti
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxParticipants}
                    onChange={(e) => handleFormChange('maxParticipants', e.target.value)}
                    className="w-full px-3 py-2 bg-light-surface-container dark:bg-dark-surface-container text-light-on-surface dark:text-dark-on-surface rounded-lg border border-light-outline dark:border-dark-outline focus:outline-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPublicEdit"
                    checked={formData.isPublic}
                    onChange={(e) => handleFormChange('isPublic', e.target.checked)}
                    className="w-4 h-4 text-light-primary dark:text-dark-primary rounded"
                  />
                  <label htmlFor="isPublicEdit" className="text-sm text-light-on-surface dark:text-dark-on-surface">
                    Visibile a tutti gli amici
                  </label>
                </div>

                {/* Invita Amici */}
                <div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 mb-3">
                    <label className="block text-sm font-medium text-light-on-surface dark:text-dark-on-surface">
                      Invita Amici ({selectedFriends.length} selezionati)
                    </label>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <button
                        type="button"
                        onClick={selectAllFriends}
                        className="flex-1 sm:flex-none px-4 py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-opacity font-medium text-sm shadow-md"
                      >
                        ✓ Invita Tutti
                      </button>
                      <button
                        type="button"
                        onClick={deselectAllFriends}
                        className="flex-1 sm:flex-none px-4 py-2 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
                      >
                        Deseleziona
                      </button>
                    </div>
                  </div>
                  {friends.length === 0 ? (
                    <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant italic">
                      Nessun amico da invitare.
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-light-outline dark:border-dark-outline rounded-lg p-2 space-y-1">
                      {friends.map(friend => (
                        <label
                          key={friend.id}
                          className="flex items-center gap-2 p-2 rounded hover:bg-light-surface-variant dark:hover:bg-dark-surface-variant cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFriends.includes(friend.id)}
                            onChange={() => toggleFriendSelection(friend.id)}
                            className="w-4 h-4 text-light-primary dark:text-dark-primary rounded"
                          />
                          <span className="text-sm text-light-on-surface dark:text-dark-on-surface">
                            {friend.firstName} {friend.lastName} ({friend.username})
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedEvent(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 transition-opacity"
                >
                  Annulla
                </button>
                <button
                  onClick={handleEditEvent}
                  className="flex-1 px-4 py-2 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                >
                  <Check className="w-5 h-5" />
                  Salva Modifiche
                </button>
              </div>
        </Modal>

      {/* Modal Dettaglio Partecipanti */}
      <Modal
        isOpen={showDetailModal && selectedEvent}
        onClose={() => setShowDetailModal(false)}
        title={`Partecipanti: ${selectedEvent?.title || ''}`}
        icon={Users}
        iconColor="text-light-primary dark:text-dark-primary"
        size="lg"
      >
        {/* Statistiche */}
        <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-light-primary/20 dark:bg-dark-primary/20 rounded-lg p-3 text-center">
                  <Users className="w-5 h-5 text-light-primary dark:text-dark-primary mx-auto mb-1" />
                  <div className="text-2xl font-bold text-light-primary dark:text-dark-primary">
                    {selectedEvent?.participantCount || 0}
                  </div>
                  <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">Invitati</div>
                </div>
                <div className="bg-green-500/20 rounded-lg p-3 text-center">
                  <UserCheck className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {selectedEvent?.acceptedCount || 0}
                  </div>
                  <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">Accettati</div>
                </div>
                <div className="bg-amber-500/20 rounded-lg p-3 text-center">
                  <HelpCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                    {selectedEvent?.maybeCount || 0}
                  </div>
                  <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">Forse</div>
                </div>
                <div className="bg-red-500/20 rounded-lg p-3 text-center">
                  <UserX className="w-5 h-5 text-red-600 dark:text-red-400 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {selectedEvent?.declinedCount || 0}
                  </div>
                  <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">Rifiutati</div>
                </div>
              </div>

              {/* Lista Partecipanti */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-light-on-surface dark:text-dark-on-surface mb-3">
                  Lista Partecipanti
                </h4>
                {selectedEvent?.participants && selectedEvent.participants.length > 0 ? (
                  selectedEvent.participants.map((participant) => {
                    const statusConfig = {
                      invited: { icon: Users, color: 'text-light-on-surface-variant dark:text-dark-on-surface-variant', bg: 'bg-light-surface-variant/50 dark:bg-dark-surface-variant/50', label: 'Invitato' },
                      accepted: { icon: UserCheck, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-500/20', label: 'Accettato' },
                      maybe: { icon: HelpCircle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/20', label: 'Forse' },
                      declined: { icon: UserX, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/20', label: 'Rifiutato' }
                    };
                    const config = statusConfig[participant.status] || statusConfig.invited;
                    const StatusIcon = config.icon;

                    return (
                      <div
                        key={participant.id}
                        className="flex items-center justify-between p-3 bg-light-surface-container dark:bg-dark-surface-container rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${config.bg}`}>
                            <StatusIcon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div>
                            <div className="font-medium text-light-on-surface dark:text-dark-on-surface">
                              {participant.user?.firstName} {participant.user?.lastName}
                            </div>
                            <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">
                              @{participant.user?.username}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-semibold ${config.color}`}>
                            {config.label}
                          </div>
                          {participant.respondedAt && (
                            <div className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant">
                              {new Date(participant.respondedAt).toLocaleDateString('it-IT', {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-light-on-surface-variant dark:text-dark-on-surface-variant">
                    Nessun partecipante invitato
                  </div>
                )}
              </div>

              {/* Chiudi */}
              <div className="mt-6">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="w-full px-4 py-2 bg-light-surface-variant dark:bg-dark-surface-variant text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 transition-opacity"
                >
                  Chiudi
                </button>
              </div>
      </Modal>
      </div>
    </div>
  );
};

export default EventManagement;
